

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Grid, Typography,
  Divider, Slider, Checkbox, FormControlLabel, Select, MenuItem, Paper, CircularProgress
} from "@mui/material";
import {
  Search as SearchIcon,
  MailOutline as MailIcon,
  ContentCopy as CopyIcon,
  TrendingFlat as ArrowIcon,
  DirectionsBus as BusIcon
} from "@mui/icons-material";
import axios from 'axios';
import "../SearchResult/BusSearchPage.css"
import Navber from '../../../Components/Navber.jsx';
import BusSearch from '../BusSearch.jsx';

export default function BusSearchPage() {
  const styles = {

    berthContainer: {
      border: '1px solid #ccc',
      p: 2,
      bgcolor: 'white',
      borderRadius: '4px',
      minHeight: '120px',
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    },
    seatIcon: {
      width: 35,
      height: 20,
      border: '1px solid #999',
      borderRadius: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      position: 'relative'
    },
    headerBg: "#FFD700",
    mainBg: "#6B5E00",
    accentPurple: "#483D8B",
    textYellow: "#FFD700",
    white: "#FFFFFF",
    borderLight: "rgba(255, 255, 255, 0.1)",

    availableSeat: { bgcolor: '#fff', border: '1px solid #ccc' },
    bookedSeat: { bgcolor: '#d3d3d3', border: '1px solid #b0b0b0' },
    selectedSeat: { bgcolor: '#b4d487', border: '1px solid #8ab354' }, // Light green
    ladiesSeat: { bgcolor: '#fff', border: '1px solid #ff69b4' },
  };
  const location = useLocation();
  const navigate = useNavigate();
   const accessToken = localStorage.getItem("accessToken");
console.log("accessToken : ",accessToken)

  // Get data from navigation state
  const { searchData, busResults } = location.state || {};
  console.log("searchData : ", searchData);

  // States
  const [fromCity, setFromCity] = useState(searchData?.from?.name || "");
  const [toCity, setToCity] = useState(searchData?.to?.name || "");
  const [journeyDate, setJourneyDate] = useState(searchData?.date || "2026-01-15");
  const [showSeats, setShowSeats] = useState({});
  const [selectedSeats, setSelectedSeats] = useState({});
  const [selectedBus, setSelectedBus] = useState(null);
  const [loading, setLoading] = useState({});
  const [seatLayouts, setSeatLayouts] = useState({}); // Store seat layouts for each bus
  const [boardingPoints, setBoardingPoints] = useState({}); // Store boarding points for each bus
  const [droppingPoints, setDroppingPoints] = useState({}); // Store dropping points for each bus
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState({}); // Selected boarding point per bus
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState({}); // Selected dropping point per bus

  console.log("busResults : ", busResults)

  // Get bus data from API response
  const buses = busResults?.BusSearchResult?.BusResults || [];
  const traceId = busResults?.BusSearchResult?.TraceId || ""; // Get TraceId from search results
  const origin = busResults?.BusSearchResult?.Origin || searchData?.from?.name || "";
  const destination = busResults?.BusSearchResult?.Destination || searchData?.to?.name || "";

  // Filter states
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 10000,
    departureStart: "00:00",
    departureEnd: "23:59",
    arrivalStart: "00:00",
    arrivalEnd: "23:59",
    liveTracking: false,
  });

  useEffect(() => {
    if (buses.length > 0) {
      // Calculate price range from actual bus data
      const prices = buses.map(bus => bus.BusPrice?.OfferedPriceRoundedOff || bus.BusPrice?.PublishedPriceRoundedOff || 0);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      setFilters(prev => ({
        ...prev,
        minPrice: minPrice > 0 ? minPrice : 0,
        maxPrice: maxPrice > 0 ? maxPrice : 10000
      }));
    }
  }, [buses]);

  // Filter buses based on filter criteria
  const filteredBuses = buses.filter(bus => {
    const price = bus.BusPrice?.OfferedPriceRoundedOff || bus.BusPrice?.PublishedPriceRoundedOff || 0;
    const departureTime = bus.DepartureTime ? new Date(bus.DepartureTime) : new Date();
    const arrivalTime = bus.ArrivalTime ? new Date(bus.ArrivalTime) : new Date();

    const departureHour = departureTime.getHours();
    const departureMinute = departureTime.getMinutes();
    const arrivalHour = arrivalTime.getHours();
    const arrivalMinute = arrivalTime.getMinutes();

    const [depStartHour, depStartMin] = filters.departureStart.split(":").map(Number);
    const [depEndHour, depEndMin] = filters.departureEnd.split(":").map(Number);
    const [arrStartHour, arrStartMin] = filters.arrivalStart.split(":").map(Number);
    const [arrEndHour, arrEndMin] = filters.arrivalEnd.split(":").map(Number);

    // Check price range
    if (price < filters.minPrice || price > filters.maxPrice) return false;

    // Check departure time
    const departureInMinutes = departureHour * 60 + departureMinute;
    const depStartInMinutes = depStartHour * 60 + depStartMin;
    const depEndInMinutes = depEndHour * 60 + depEndMin;
    if (departureInMinutes < depStartInMinutes || departureInMinutes > depEndInMinutes) return false;

    // Check arrival time
    const arrivalInMinutes = arrivalHour * 60 + arrivalMinute;
    const arrStartInMinutes = arrStartHour * 60 + arrStartMin;
    const arrEndInMinutes = arrEndHour * 60 + arrEndMin;
    if (arrivalInMinutes < arrStartInMinutes || arrivalInMinutes > arrEndInMinutes) return false;

    // Check live tracking
    if (filters.liveTracking && !bus.LiveTrackingAvailable) return false;

    return true;
  });

  // Format time from ISO string
  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format date from ISO string
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  // Update the formatCurrency function to handle negative values better
  const formatCurrency = (amount) => {
    const displayAmount = amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(displayAmount);
  };


  // Get travel name initials
  const getTravelInitials = (travelName) => {
    if (!travelName) return "?";
    return travelName.charAt(0).toUpperCase();
  };

  // Handle seat selection
  const handleSeatClick = (busId, seatId) => {
    setSelectedSeats(prev => {
      const busSeats = prev[busId] || [];
      const newSeats = busSeats.includes(seatId)
        ? busSeats.filter(s => s !== seatId)
        : [...busSeats, seatId];

      return {
        ...prev,
        [busId]: newSeats
      };
    });
  };

  // API call to get seat layout
  const fetchSeatLayout = async (busId) => {
    try {
      if(!accessToken){
        navigate('/signup');
        return;
      }
      setLoading(prev => ({ ...prev, [busId]: true }));

      const formData = new FormData();
      formData.append('ResultIndex', busId.toString());
      formData.append('TraceId', traceId);

      const response = await axios.post('https://admin.jayrajgroup.com/api/bus/bus-seat-layout', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      console.log("Seat layout API response for bus", busId, ":", response.data);

      if (response.data?.data?.SeatLayoutDetails?.SeatLayout?.SeatDetails) {
        const seatData = response.data.data.SeatLayoutDetails.SeatLayout.SeatDetails;
        // Store seat layout in state
        setSeatLayouts(prev => ({
          ...prev,
          [busId]: seatData
        }));
        return seatData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching seat layout:', error);
      // Fallback to generated seats if API fails
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [busId]: false }));
    }
  };

  // API call to get boarding and dropping points
  const fetchBusPoints = async (busId) => {
    try {
      setLoading(prev => ({ ...prev, [busId]: true }));

      const formData = new FormData();
      formData.append('ResultIndex', busId.toString());
      formData.append('TraceId', traceId);

      const response = await axios.post('https://admintest.jayrajgroup.org/api/bus/bus-boarding-points', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        }
      });

      console.log("Bus points API response for bus", busId, ":", response.data);

      // Updated: Access data directly from response.data.data
      if (response.data?.data) {
        const result = response.data.data;

        // Store boarding points
        if (result.BoardingPointsDetails && Array.isArray(result.BoardingPointsDetails)) {
          setBoardingPoints(prev => ({
            ...prev,
            [busId]: result.BoardingPointsDetails
          }));

          // Auto-select first boarding point
          if (result.BoardingPointsDetails.length > 0 && !selectedBoardingPoint[busId]) {
            setSelectedBoardingPoint(prev => ({
              ...prev,
              [busId]: result.BoardingPointsDetails[0].CityPointIndex
            }));
          }
        }

        // Store dropping points
        if (result.DroppingPointsDetails && Array.isArray(result.DroppingPointsDetails)) {
          setDroppingPoints(prev => ({
            ...prev,
            [busId]: result.DroppingPointsDetails
          }));

          // Auto-select first dropping point
          if (result.DroppingPointsDetails.length > 0 && !selectedDroppingPoint[busId]) {
            setSelectedDroppingPoint(prev => ({
              ...prev,
              [busId]: result.DroppingPointsDetails[0].CityPointIndex
            }));
          }
        }

        return result;
      }
      return null;
    } catch (error) {
      console.error('Error fetching bus points:', error);
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [busId]: false }));
    }
  };

  // Parse seat data from API response
  const parseSeatData = (seatLayoutData) => {
    if (!seatLayoutData || !Array.isArray(seatLayoutData)) {
      return { upper: [], lower: [] };
    }

    const upperSeats = [];
    const lowerSeats = [];

    // Parse the 2D array structure from API
    seatLayoutData.forEach((row, rowIndex) => {
      if (Array.isArray(row)) {
        row.forEach((seat, colIndex) => {
          if (seat && seat.SeatName) {
            const seatObj = {
              id: seat.SeatName,
              seatNumber: seat.SeatName,
              row: parseInt(seat.RowNo || rowIndex),
              column: parseInt(seat.ColumnNo || colIndex),
              isUpper: seat.IsUpper,
              isLadiesSeat: seat.IsLadiesSeat,
              isMalesSeat: seat.IsMalesSeat,
              seatStatus: seat.SeatStatus, // true = available, false = booked
              seatType: seat.SeatType,
              price: seat.Price,
              seatFare: seat.SeatFare,
              width: seat.Width || 1,
              height: seat.Height || 2
            };

            if (seat.IsUpper) {
              upperSeats.push(seatObj);
            } else {
              lowerSeats.push(seatObj);
            }
          }
        });
      }
    });

    return { upper: upperSeats, lower: lowerSeats };
  };

  // Generate seat layout based on API response
  const generateSeatLayout = (bus) => {
    const busId = bus.ResultIndex;

    // Check if we have seat layout from API
    if (seatLayouts[busId]) {
      return parseSeatData(seatLayouts[busId]);
    }

    // Fallback: generate empty layout
    return { upper: [], lower: [] };
  };

  // Get selected boarding point details
  const getSelectedBoardingPoint = (busId) => {
    if (!selectedBoardingPoint[busId]) return null;

    const points = boardingPoints[busId];
    if (!points) return null;

    return points.find(point => point.CityPointIndex === selectedBoardingPoint[busId]);
  };

  // Get selected dropping point details
  const getSelectedDroppingPoint = (busId) => {
    if (!selectedDroppingPoint[busId]) return null;

    const points = droppingPoints[busId];
    if (!points) return null;

    return points.find(point => point.CityPointIndex === selectedDroppingPoint[busId]);
  };

  

  // Inside the SeatIcon component, add this:
  const SeatIcon = ({ seat, busId, basePrice }) => {
    const isSelected = selectedSeats[busId]?.includes(seat.seatNumber) || false;
    const isBooked = !seat.seatStatus; // seatStatus: true = available, false = booked
    const isLadies = seat.isLadiesSeat;
    const isMale = seat.isMalesSeat;

    // Get seat price from API or use base price
    const seatPrice = Math.abs(seat.seatFare || seat.price || basePrice || 0);

    let bgColor = '#fff';
    let borderColor = '#4CAF50';
    let hoverEffect = true;

    if (isBooked) {
      bgColor = '#d3d3d3';
      borderColor = '#999';
      hoverEffect = false;
    } else if (isLadies) {
      bgColor = '#fff';
      borderColor = '#FF69B4';
    } else if (isMale) {
      bgColor = '#fff';
      borderColor = '#2196F3';
    }

    if (isSelected) {
      bgColor = '#b4d487';
      borderColor = '#FF9800';
    }

    return (
      <Box
        onClick={() => !isBooked && handleSeatClick(busId, seat.seatNumber)}
        sx={{
          width: 45,
          height: 25,
          m: 0.5,
          border: `2px solid ${borderColor}`,
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isBooked ? 'not-allowed' : 'pointer',
          bgcolor: bgColor,
          position: 'relative',
          transition: 'all 0.2s ease',
          '&:hover .seat-tooltip': {
            opacity: 1,
            visibility: 'visible',
            bottom: '120%',
          }
        }}
      >
        {/* Price Tooltip on Hover */}
        {!isBooked && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: '#333',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              whiteSpace: 'nowrap',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              mb: 1,
              zIndex: 10,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '100%',
                left: '50%',
                marginLeft: '-4px',
                borderWidth: '4px',
                borderStyle: 'solid',
                borderColor: '#333 transparent transparent transparent'
              },
              '&:hover': {
                opacity: 1,
                visibility: 'visible',
                bottom: 'calc(100% + 5px)'
              }
            }}
          >
            ₹{seatPrice}
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 8,
            height: '70%',
            border: `1px solid ${borderColor}`,
            borderRadius: '2px',
            bgcolor: bgColor
          }}
        />



        {!isBooked && (
          <Box
            className="seat-tooltip" // Target this class for hover
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: '#333',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: '4px',
              fontSize: '10px',
              whiteSpace: 'nowrap',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              zIndex: 10,
              pointerEvents: 'none', // Prevents tooltip from flickering
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '100%',
                left: '50%',
                ml: '-4px',
                borderWidth: '4px',
                borderStyle: 'solid',
                borderColor: '#333 transparent transparent transparent'
              }
            }}
          >
            ₹{seatPrice}
          </Box>
        )}
      </Box>
    );
  };

  // Replace the renderSeatGrid function (around line 654)
  const renderSeatGrid = (seats, berthType, basePrice) => {
    if (!seats || seats.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No {berthType} seats available
          </Typography>
        </Box>
      );
    }

    // Group seats by row
    const seatsByRow = {};
    seats.forEach(seat => {
      if (!seatsByRow[seat.row]) {
        seatsByRow[seat.row] = [];
      }
      seatsByRow[seat.row].push(seat);
    });

    // Sort rows and sort seats within each row by column
    const sortedRows = Object.keys(seatsByRow).sort((a, b) => parseInt(a) - parseInt(b));

    return sortedRows.map(rowKey => {
      const rowSeats = seatsByRow[rowKey].sort((a, b) => parseInt(a.column) - parseInt(b.column));

      return (
        <Box key={`row-${rowKey}`} sx={{ mb: 1 }}>
          <Grid container spacing={0.5} justifyContent="center">
            {rowSeats.map(seat => (
              <Grid item key={seat.id}>
                <SeatIcon
                  seat={seat}
                  busId={selectedBus?.ResultIndex}
                  basePrice={basePrice}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    });
  };

  // Handle search button click
  const handleSearch = () => {
    navigate('/');
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset all filters
  const resetFilters = () => {
    if (buses.length > 0) {
      const prices = buses.map(bus => bus.BusPrice?.OfferedPriceRoundedOff || bus.BusPrice?.PublishedPriceRoundedOff || 0);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      setFilters({
        minPrice: minPrice > 0 ? minPrice : 0,
        maxPrice: maxPrice > 0 ? maxPrice : 10000,
        departureStart: "00:00",
        departureEnd: "23:59",
        arrivalStart: "00:00",
        arrivalEnd: "23:59",
        liveTracking: false,
      });
    }
  };

  // Handle boarding point selection
  const handleBoardingPointChange = (busId, pointId) => {
    setSelectedBoardingPoint(prev => ({
      ...prev,
      [busId]: pointId
    }));
  };

  // Handle dropping point selection
  const handleDroppingPointChange = (busId, pointId) => {
    setSelectedDroppingPoint(prev => ({
      ...prev,
      [busId]: pointId
    }));
  };

  // Handle book now button click
  // Replace the calculateTotalPrice function (around line 789)
  const calculateTotalPrice = (busId, seatLayoutData, basePriceValue) => {
    let total = 0;
    const busSeats = selectedSeats[busId] || [];

    if (busSeats.length === 0 || !seatLayoutData) return total;

    // Parse seat layout if needed
    let seatLayout;
    if (Array.isArray(seatLayoutData)) {
      seatLayout = parseSeatData(seatLayoutData);
    } else {
      seatLayout = seatLayoutData;
    }

    busSeats.forEach(seatNum => {
      // Find seat in layout to get individual price
      const seatDetail = seatLayout.upper?.find(s => s.seatNumber === seatNum) ||
        seatLayout.lower?.find(s => s.seatNumber === seatNum);
      const seatPrice = seatDetail?.seatFare || seatDetail?.price || basePriceValue || 0;
      total += seatPrice;
    });

    return total;
  };

  const handleBookNow = async (bus) => {
    const busId = bus.ResultIndex;
    const busBasePrice = bus.BusPrice?.OfferedPriceRoundedOff || bus.BusPrice?.PublishedPriceRoundedOff || 0;
    const busSelectedSeats = selectedSeats[busId] || [];

    // Validate seats are selected
    if (busSelectedSeats.length === 0) {
      alert("Please select at least one seat");
      return;
    }

    // Validate boarding point is selected
    const boardingPoint = getSelectedBoardingPoint(busId);
    if (!boardingPoint) {
      alert("Please select a boarding point");
      return;
    }

    // Validate dropping point is selected
    const droppingPoint = getSelectedDroppingPoint(busId);
    if (!droppingPoint) {
      alert("Please select a dropping point");
      return;
    }

    try {
      setLoading(prev => ({ ...prev, [busId]: true }));

      // First fetch seat layout if not already fetched
      let seatLayoutData = seatLayouts[busId];
      if (!seatLayoutData) {
        seatLayoutData = await fetchSeatLayout(busId);
      }

      // Get seat details for each selected seat
      const selectedSeatDetails = [];
      if (seatLayoutData) {
        // Parse seat layout to get individual seat details
        seatLayoutData.forEach((row) => {
          if (Array.isArray(row)) {
            row.forEach((seat) => {
              if (seat && seat.SeatName && busSelectedSeats.includes(seat.SeatName)) {
                selectedSeatDetails.push(seat);
              }
            });
          }
        });
      }

      // Calculate total price
      const totalPrice = calculateTotalPrice(busId, seatLayoutData, busBasePrice);

      // Navigate to passenger details page with all necessary data
      navigate('/passenger-details', {
        state: {
          bus,
          selectedSeats: busSelectedSeats,
          selectedSeatDetails: selectedSeatDetails,
          seatLayout: seatLayoutData,
          boardingPoint,
          droppingPoint,
          totalPrice: totalPrice || busSelectedSeats.length * busBasePrice,
          searchData,
          traceId,
          // Additional data needed for APIs
          origin: searchData?.from?.name || origin,
          destination: searchData?.to?.name || destination,
          departDate: searchData?.date || journeyDate
        }
      });

    } catch (error) {
      console.error('Error during booking:', error);
      alert('Failed to process booking. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [busId]: false }));
    }
  };

  // Handle showing seats (fetch layout if not already fetched)
  const handleShowSeats = async (bus) => {
    const busId = bus.ResultIndex;

    // Set as selected bus
    setSelectedBus(bus);

    // If seats are not already visible, fetch layout and points
    if (!showSeats[busId]) {
      // Fetch seat layout
      if (!seatLayouts[busId]) {
        await fetchSeatLayout(busId);
      }

      // Fetch boarding and dropping points
      if (!boardingPoints[busId] || !droppingPoints[busId]) {
        await fetchBusPoints(busId);
      }
    }

    // Toggle seat visibility
    setShowSeats(prev => ({
      ...prev,
      [busId]: !prev[busId]
    }));
  };

  if (!searchData || !busResults) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: styles.mainBg
      }}>
        <CircularProgress sx={{ color: styles.headerBg }} />
        <Typography variant="h6" color={styles.textYellow} sx={{ ml: 2 }}>
          Loading bus results...
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ bgcolor: styles.mainBg, minHeight: '100vh', width: '100%' }}>
      <Navber/>
      {/* Top Search Bar Section - Full Width */}
      {/* <Box sx={{ bgcolor: styles.headerBg, p: 2, width: '100%' }}>
        <Grid container spacing={0} sx={{ width: '100%', alignItems: 'center' }}>
          <Grid item xs={12} md={3.5} sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
            <Typography variant="caption" fontWeight="bold">From</Typography>
            <TextField
              variant="standard"
              fullWidth
              value={fromCity}
              onChange={(e) => setFromCity(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
              }}
              sx={{ bgcolor: 'white', p: 1, borderRight: '1px solid #ccc' }}
            />
          </Grid>
          <Grid item xs={12} md={3.5} sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
            <Typography variant="caption" fontWeight="bold">To</Typography>
            <TextField
              variant="standard"
              fullWidth
              value={toCity}
              onChange={(e) => setToCity(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
              }}
              sx={{ bgcolor: 'white', p: 1, borderRight: '1px solid #ccc' }}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
            <Typography variant="caption" fontWeight="bold">Depart</Typography>
            <TextField
              type="date"
              variant="standard"
              fullWidth
              value={journeyDate}
              onChange={(e) => setJourneyDate(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{ bgcolor: 'white', p: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSearch}
              sx={{ bgcolor: styles.accentPurple, height: '48px', borderRadius: 0, textTransform: 'none', fontSize: '1.2rem', mt: 2 }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box> */}
      <div style={{display:"flex",justifyContent:"start"}}>
<BusSearch initialSearchData={searchData}/>

      </div>
<hr/>
      {/* Main Layout */}
      <Box sx={{ width: '100%', p: 0 }}>
        <Grid container sx={{ minHeight: 'calc(100vh - 100px)' }}>

          {/* Sidebar - Fixed Width/Ratio */}
          <Grid item xs={12} md={2.5} sx={{ p: 3, borderRight: `1px solid ${styles.borderLight}`, position: 'sticky', top: 0, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" color="#8B8000" fontWeight="bold">Filter your Search</Typography>
              <Typography
                variant="caption"
                color="red"
                sx={{ cursor: 'pointer' }}
                onClick={resetFilters}
              >
                Reset All
              </Typography>
            </Box>
            <Divider sx={{ bgcolor: '#8B8000', my: 1 }} />

            <Typography variant="body2" color={styles.textYellow} fontWeight="bold" sx={{ mt: 2 }}>Live Tracking</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filters.liveTracking}
                  onChange={(e) => handleFilterChange('liveTracking', e.target.checked)}
                  sx={{ color: 'white', '&.Mui-checked': { color: styles.textYellow } }}
                />
              }
              label={
                <Typography variant="caption" color="rgba(255,255,255,0.6)">
                  Live Tracking ({buses.filter(bus => bus.LiveTrackingAvailable).length})
                </Typography>
              }
            />

            <Divider sx={{ bgcolor: styles.borderLight, my: 2 }} />

            <Typography variant="body2" color={styles.textYellow} fontWeight="bold" sx={{ mt: 1 }}>Price Range</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color={styles.textYellow}>
                {formatCurrency(filters.minPrice)}
              </Typography>
              <Typography variant="caption" color={styles.textYellow}>
                {formatCurrency(filters.maxPrice)}
              </Typography>
            </Box>
            <Slider
              size="small"
              value={[filters.minPrice, filters.maxPrice]}
              onChange={(e, newValue) => {
                handleFilterChange('minPrice', newValue[0]);
                handleFilterChange('maxPrice', newValue[1]);
              }}
              min={0}
              max={Math.max(filters.maxPrice * 2, 10000)}
              valueLabelDisplay="auto"
              valueLabelFormat={formatCurrency}
              sx={{ color: styles.textYellow }}
            />

            <Typography variant="body2" color={styles.textYellow} fontWeight="bold" sx={{ mt: 3 }}>Departure Time</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                type="time"
                size="small"
                value={filters.departureStart}
                onChange={(e) => handleFilterChange('departureStart', e.target.value)}
                InputProps={{ sx: { color: styles.textYellow, fontSize: '0.75rem' } }}
                sx={{ width: '50%' }}
              />
              <TextField
                type="time"
                size="small"
                value={filters.departureEnd}
                onChange={(e) => handleFilterChange('departureEnd', e.target.value)}
                InputProps={{ sx: { color: styles.textYellow, fontSize: '0.75rem' } }}
                sx={{ width: '50%' }}
              />
            </Box>

            <Typography variant="body2" color={styles.textYellow} fontWeight="bold" sx={{ mt: 3 }}>Arrival Time</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                type="time"
                size="small"
                value={filters.arrivalStart}
                onChange={(e) => handleFilterChange('arrivalStart', e.target.value)}
                InputProps={{ sx: { color: styles.textYellow, fontSize: '0.75rem' } }}
                sx={{ width: '50%' }}
              />
              <TextField
                type="time"
                size="small"
                value={filters.arrivalEnd}
                onChange={(e) => handleFilterChange('arrivalEnd', e.target.value)}
                InputProps={{ sx: { color: styles.textYellow, fontSize: '0.75rem' } }}
                sx={{ width: '50%' }}
              />
            </Box>
          </Grid>

          {/* Main Content Area */}
          <Box sx={{ width: '80%', p: 4 }}>
            <Typography variant="h6" color="white">
              {filteredBuses.length} Bus{filteredBuses.length !== 1 ? 'es' : ''} available
            </Typography>

            {filteredBuses.length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, mt: 2, bgcolor: 'white', textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No buses found matching your filters
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your filter criteria
                </Typography>
              </Paper>
            ) : (
              filteredBuses.map((bus, index) => {
                const busId = bus.ResultIndex;
                const isSeatsVisible = showSeats[busId] || false;
                const busSeats = selectedSeats[busId] || [];
                const basePrice = bus.BusPrice?.OfferedPriceRoundedOff || bus.BusPrice?.PublishedPriceRoundedOff || 0;
                const seatLayout = generateSeatLayout(bus);
                const isLoading = loading[busId];
                const busBoardingPoints = boardingPoints[busId] || [];
                const busDroppingPoints = droppingPoints[busId] || [];
                const selectedBoarding = selectedBoardingPoint[busId];
                const selectedDropping = selectedDroppingPoint[busId];

                return (
                  <Paper
                    key={busId || index}
                    elevation={0}
                    sx={{ borderRadius: 0, overflow: 'hidden', mt: 2, bgcolor: 'white' }}
                  >
                    {/* Bus Header - Compact */}
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          bgcolor: styles.headerBg,
                          width: 45,
                          height: 45,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'black',
                          fontWeight: 'bold',
                          borderRadius: '4px'
                        }}>
                          {getTravelInitials(bus.TravelName)}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {bus.TravelName || "Unknown Travels"}
                          </Typography>
                          <Typography variant="caption" color="gray">
                            {bus.BusType || "Bus Type Not Available"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Time and Date */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {formatTime(bus.DepartureTime)}
                        </Typography>
                        <Typography variant="caption" color="gray">
                          {formatDate(bus.DepartureTime)}
                        </Typography>
                      </Box>

                      <ArrowIcon sx={{ color: '#666' }} />

                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {formatTime(bus.ArrivalTime)}
                        </Typography>
                        <Typography variant="caption" color="gray">
                          {formatDate(bus.ArrivalTime)}
                        </Typography>
                      </Box>

                      {/* Seat Count and Price */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {bus.AvailableSeats || 0}
                        </Typography>
                        <Typography variant="caption" color="gray">Seat{bus.AvailableSeats !== 1 ? 's' : ''}</Typography>
                      </Box>

                      {/* <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color={styles.headerBg}>
                          {formatCurrency(basePrice)}
                        </Typography>
                      </Box> */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color={styles.headerBg}>
                          {busSeats.length > 0
                            ? formatCurrency(busSeats.reduce((total, seatNum) => {
                              const seatDetail = seatLayout.upper?.find(s => s.seatNumber === seatNum) ||
                                seatLayout.lower?.find(s => s.seatNumber === seatNum);
                              const seatPrice = Math.abs(seatDetail?.seatFare || seatDetail?.price || basePrice || 0);
                              return total + seatPrice;
                            }, 0))
                            : formatCurrency(Math.abs(basePrice))}
                        </Typography>
                        {busSeats.length > 0 && (
                          <Typography variant="caption" color="gray">
                            {busSeats.length} seat{busSeats.length !== 1 ? 's' : ''} selected
                          </Typography>
                        )}
                      </Box>

                      {/* Book Now Button */}
                      <Button
                        variant="contained"
                        onClick={() => handleShowSeats(bus)}
                        disabled={isLoading}
                        sx={{
                          bgcolor: styles.headerBg,
                          color: 'black',
                          fontWeight: 'bold',
                          minWidth: '140px',
                          '&:hover': { bgcolor: '#FFC800' },
                          '&.Mui-disabled': {
                            bgcolor: '#E0E0E0',
                            color: '#999'
                          }
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} sx={{ color: 'black' }} />
                        ) : isSeatsVisible ? (
                          "Close"
                        ) : busSeats.length > 0 ? (
                          `Book ${busSeats.length} Seat${busSeats.length !== 1 ? 's' : ''}`
                        ) : (
                          "Book Now"
                        )}
                      </Button>
                    </Box>

                    {/* Footer Links */}
                    <Box sx={{
                      bgcolor: '#fafafa',
                      p: 1.5,
                      borderTop: '1px solid #eee',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" color="gray">
                        <span style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                          Boarding Point: {bus.BoardingPointsDetails?.[0]?.CityPointName || "Not Available"}
                        </span> |
                        <span
                          style={{ cursor: 'pointer', marginLeft: '8px', fontWeight: 'bold' }}
                          onClick={() => handleShowSeats(bus)}
                        >
                          Select Seat
                        </span> |
                        <span style={{ cursor: 'pointer', marginLeft: '8px', fontWeight: 'bold' }}>
                          Cancellation Policy
                        </span>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#007bff', cursor: 'pointer' }}>
                          <MailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">Email me</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <CopyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">Copy to Clipboard</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Seat Selection Section */}
                    {isSeatsVisible && (
                      <Box sx={{ bgcolor: '#F9F9F9', p: 3, borderTop: '1px solid #eee' }}>
                        <Grid container spacing={2}>
                          {/* Left - Seat Layout */}
                          <Grid item xs={12} md={8}>
                            {isLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                <CircularProgress />
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  Loading seat layout...
                                </Typography>
                              </Box>
                            ) : (
                              <Grid container spacing={2}>
                                {/* Upper Berth */}
                                <Grid item xs={12}>
                                  <Box sx={{ position: 'relative', mb: 3 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        position: 'absolute',
                                        left: -20,
                                        top: '50%',
                                        transform: 'rotate(-90deg)',
                                        color: '#666',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}
                                    >
                                      UPPER BERTH
                                    </Typography>
                                    <Box sx={{
                                      ml: 4,
                                      border: '2px solid #DDD',
                                      p: 3,
                                      bgcolor: 'white',
                                      borderRadius: '8px',
                                      minHeight: '200px'
                                    }}>
                                      {renderSeatGrid(seatLayout.upper, "upper", basePrice)}
                                    </Box>
                                  </Box>
                                </Grid>

                                {/* Lower Berth */}
                                <Grid item xs={12}>
                                  <Box sx={{ position: 'relative' }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        position: 'absolute',
                                        left: -20,
                                        top: '50%',
                                        transform: 'rotate(-90deg)',
                                        color: '#666',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}
                                    >
                                      LOWER BERTH
                                    </Typography>
                                    <Box sx={{
                                      ml: 4,
                                      border: '2px solid #DDD',
                                      p: 3,
                                      bgcolor: 'white',
                                      borderRadius: '8px',
                                      minHeight: '200px'
                                    }}>
                                      {renderSeatGrid(seatLayout.lower, "lower", basePrice)}
                                    </Box>
                                  </Box>
                                </Grid>

                                {/* Legend */}
                                {/* <Grid item xs={12} sx={{ mt: 3, ml: 4 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: '#555' }}>
                                    Seat Legend
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 24, height: 24, border: '2px solid #4CAF50', bgcolor: styles.available, borderRadius: '4px' }} />
                                      <Typography variant="caption" fontWeight="medium">Available</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 24, height: 24, border: '2px solid #FF69B4', bgcolor: styles.ladies, borderRadius: '4px' }} />
                                      <Typography variant="caption" fontWeight="medium">Ladies Seat</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 24, height: 24, border: '2px solid #2196F3', bgcolor: styles.male, borderRadius: '4px' }} />
                                      <Typography variant="caption" fontWeight="medium">Male Seat</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 24, height: 24, border: '2px solid #FF9800', bgcolor: styles.selected, borderRadius: '4px' }} />
                                      <Typography variant="caption" fontWeight="medium">Selected</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 24, height: 24, border: '2px solid #999', bgcolor: styles.booked, borderRadius: '4px' }} />
                                      <Typography variant="caption" fontWeight="medium">Booked</Typography>
                                    </Box>
                                  </Box>
                                </Grid> */}

                              </Grid>
                            )}
                          </Grid>
                          <Grid container spacing={2} sx={{ p: 2, alignItems: 'stretch' }} style={{ width: "100%" }}>
                            {/* Left - Seat Legend Container */}
                            <Grid item xs={12} md={3}>
                              <Box sx={{
                                border: '1px solid #ccc',
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5
                              }}>
                                {/* Legend Items */}
                                {[
                                  { label: 'Available seat', color: '#fff', border: '#ccc' },
                                  { label: 'Reserved for ladies', color: '#fff', border: '#FF69B4' },
                                  { label: 'Selected seat', color: '#8BC34A', border: '#689F38' },
                                  { label: 'Booked seat', color: '#DDD', border: '#BBB' }
                                ].map((item) => (
                                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                      width: 24,
                                      height: 18,
                                      bgcolor: item.color,
                                      border: `1px solid ${item.border}`,
                                      borderRadius: '4px',
                                      position: 'relative',
                                      '&::after': { // This adds the "seat handle" look from Image 1
                                        content: '""',
                                        position: 'absolute',
                                        right: -4,
                                        top: '20%',
                                        height: '60%',
                                        width: 4,
                                        bgcolor: item.border,
                                        borderRadius: '0 2px 2px 0'
                                      }
                                    }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#333' }}>
                                      {item.label}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>

                            {/* Right - Booking Summary Container */}
                            <Grid item xs={12} md={8} style={{ width: "70%" }}>

                              {(() => {
                                const totalPrice = busSeats.reduce((total, seatNum) => {
                                  const seatDetail = seatLayout.upper?.find(s => s.seatNumber === seatNum) ||
                                    seatLayout.lower?.find(s => s.seatNumber === seatNum);
                                  const seatPrice = Math.abs(seatDetail?.seatFare || seatDetail?.price || basePrice || 0);
                                  return total + seatPrice;
                                }, 0);

                                return (
                                  <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: '2px' }}>
                                    {/* Seat and Price Info - Styled like Image 1 */}
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        Seat(s) :<span style={{ fontWeight: 'normal', marginLeft: '4px' }}>
                                          {busSeats.length > 0 ? busSeats.join(', ') : ''}
                                        </span>
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Base Price :<span style={{ fontWeight: 'normal', marginLeft: '4px' }}>
                                          {formatCurrency(totalPrice)}
                                        </span>
                                      </Typography>
                                    </Box>

                                    {/* Dropdowns side-by-side */}
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6} style={{display:"flex", gap:"20px", width:"100%"}}>
                                        <div style={{width:"45%"}}>
                                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                          Select Boarding Point
                                        </Typography>
                                        <Select
                                          fullWidth
                                          size="small"
                                          value={selectedBoarding || ""}
                                          onChange={(e) => handleBoardingPointChange(busId, e.target.value)}
                                          displayEmpty
                                          sx={{ mb: 3 }}
                                          disabled={busBoardingPoints.length === 0 || isLoading}
                                          renderValue={(selected) => {
                                            if (!selected) {
                                              return <Typography variant="body2" color="text.secondary">Select Boarding Point</Typography>;
                                            }
                                            const selectedPoint = busBoardingPoints.find(p => p.CityPointIndex === selected);
                                            if (selectedPoint) {
                                              return `${selectedPoint.CityPointName} - ${formatTime(selectedPoint.CityPointTime)}`;
                                            }
                                            return selected;
                                          }}
                                        >
                                          {busBoardingPoints.length === 0 ? (
                                            <MenuItem value="" disabled>
                                              {isLoading ? "Loading..." : "No boarding points available"}
                                            </MenuItem>
                                          ) : (
                                            busBoardingPoints.map((point) => (
                                              <MenuItem key={point.CityPointIndex} value={point.CityPointIndex}>
                                                <Box>
                                                  <Typography variant="body2">
                                                    {point.CityPointName} - {formatTime(point.CityPointTime)}
                                                  </Typography>

                                                </Box>
                                              </MenuItem>
                                            ))
                                          )}
                                        </Select>
</div>

<div style={{width:"45%"}}>
                                        {/* Dropping Points Dropdown */}
                                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                          Select Dropping Point
                                        </Typography>
                                        <Select
                                          fullWidth
                                          size="small"
                                          value={selectedDropping || ""}
                                          onChange={(e) => handleDroppingPointChange(busId, e.target.value)}
                                          displayEmpty
                                          sx={{ mb: 3 }}
                                          disabled={busDroppingPoints.length === 0 || isLoading}
                                          renderValue={(selected) => {
                                            if (!selected) {
                                              return <Typography variant="body2" color="text.secondary">Select Dropping Point</Typography>;
                                            }
                                            const selectedPoint = busDroppingPoints.find(p => p.CityPointIndex === selected);
                                            if (selectedPoint) {
                                              return `${selectedPoint.CityPointName} - ${formatTime(selectedPoint.CityPointTime)}`;
                                            }
                                            return selected;
                                          }}
                                        >
                                          {busDroppingPoints.length === 0 ? (
                                            <MenuItem value="" disabled>
                                              {isLoading ? "Loading..." : "No dropping points available"}
                                            </MenuItem>
                                          ) : (
                                            busDroppingPoints.map((point) => (
                                              <MenuItem key={point.CityPointIndex} value={point.CityPointIndex}>
                                                <Box>
                                                  <Typography variant="body2">
                                                    {point.CityPointName} - {formatTime(point.CityPointTime)}
                                                  </Typography>

                                                </Box>
                                              </MenuItem>
                                            ))
                                          )}
                                        </Select>
</div>
                                      </Grid>


                                    </Grid>

                                    {/* Action Button - Note: 1st image shows no button, but this keeps your flow */}
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      disabled={busSeats.length === 0 || !selectedBoarding || !selectedDropping}
                                      onClick={() => handleBookNow(bus)}
                                      sx={{
                                        mt: 3,
                                        bgcolor: '#FFD700',
                                        color: '#000',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        '&:hover': { bgcolor: '#FFC800', boxShadow: 'none' }
                                      }}
                                    >
                                      Proceed to Book
                                    </Button>
                                  </Box>
                                );
                              })()}
                            </Grid>
                          </Grid>
                          {/* Right - Booking Summary */}
                          {/* <Grid item xs={12} md={4}>
                            <Box sx={{
                              bgcolor: 'white',
                              border: '2px solid #DDD',
                              p: 3,
                              borderRadius: '8px',
                              height: '100%',
                              position: 'sticky',
                              top: 20
                            }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#333' }}>
                                Booking Summary
                              </Typography>

                             

                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                  Selected Seats: {busSeats.length > 0 ? busSeats.join(', ') : 'None'}
                                </Typography>

                                <Typography variant="subtitle2" fontWeight="bold">
                                  Total: {formatCurrency(busSeats.reduce((total, seatNum) => {
                                    const seatDetail = seatLayout.upper?.find(s => s.seatNumber === seatNum) ||
                                      seatLayout.lower?.find(s => s.seatNumber === seatNum);
                                    const seatPrice = Math.abs(seatDetail?.seatFare || seatDetail?.price || basePrice || 0);
                                    return total + seatPrice;
                                  }, 0))}
                                </Typography>
                              </Box>


                              <Divider sx={{ my: 2 }} />


                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                Select Boarding Point
                              </Typography>
                              <Select
                                fullWidth
                                size="small"
                                value={selectedBoarding || ""}
                                onChange={(e) => handleBoardingPointChange(busId, e.target.value)}
                                displayEmpty
                                sx={{ mb: 3 }}
                                disabled={busBoardingPoints.length === 0 || isLoading}
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return <Typography variant="body2" color="text.secondary">Select Boarding Point</Typography>;
                                  }
                                  const selectedPoint = busBoardingPoints.find(p => p.CityPointIndex === selected);
                                  if (selectedPoint) {
                                    return `${selectedPoint.CityPointName} - ${formatTime(selectedPoint.CityPointTime)}`;
                                  }
                                  return selected;
                                }}
                              >
                                {busBoardingPoints.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    {isLoading ? "Loading..." : "No boarding points available"}
                                  </MenuItem>
                                ) : (
                                  busBoardingPoints.map((point) => (
                                    <MenuItem key={point.CityPointIndex} value={point.CityPointIndex}>
                                      <Box>
                                        <Typography variant="body2">
                                          {point.CityPointName} - {formatTime(point.CityPointTime)}
                                        </Typography>

                                      </Box>
                                    </MenuItem>
                                  ))
                                )}
                              </Select>




                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                Select Dropping Point
                              </Typography>
                              <Select
                                fullWidth
                                size="small"
                                value={selectedDropping || ""}
                                onChange={(e) => handleDroppingPointChange(busId, e.target.value)}
                                displayEmpty
                                sx={{ mb: 3 }}
                                disabled={busDroppingPoints.length === 0 || isLoading}
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return <Typography variant="body2" color="text.secondary">Select Dropping Point</Typography>;
                                  }
                                  const selectedPoint = busDroppingPoints.find(p => p.CityPointIndex === selected);
                                  if (selectedPoint) {
                                    return `${selectedPoint.CityPointName} - ${formatTime(selectedPoint.CityPointTime)}`;
                                  }
                                  return selected;
                                }}
                              >
                                {busDroppingPoints.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    {isLoading ? "Loading..." : "No dropping points available"}
                                  </MenuItem>
                                ) : (
                                  busDroppingPoints.map((point) => (
                                    <MenuItem key={point.CityPointIndex} value={point.CityPointIndex}>
                                      <Box>
                                        <Typography variant="body2">
                                          {point.CityPointName} - {formatTime(point.CityPointTime)}
                                        </Typography>

                                      </Box>
                                    </MenuItem>
                                  ))
                                )}
                              </Select>



                              <Button
                                variant="contained"
                                fullWidth
                                sx={{
                                  bgcolor: styles.headerBg,
                                  color: 'black',
                                  py: 1.5,
                                  mt: 2,
                                  fontWeight: 'bold',
                                  fontSize: '1rem',
                                  '&:hover': { bgcolor: '#FFC800' },
                                  '&.Mui-disabled': {
                                    bgcolor: '#E0E0E0',
                                    color: '#999'
                                  }
                                }}
                                disabled={busSeats.length === 0 || !selectedBoarding || !selectedDropping || isLoading}
                                onClick={() => handleBookNow(bus)}
                              >
                                {busSeats.length > 0 && selectedBoarding && selectedDropping ? "Proceed to Book" : "Complete Selection"}
                              </Button>
                            </Box>
                          </Grid> */}
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                );
              })
            )}
          </Box>
        </Grid>
      </Box>
    </Box>
  );
}